import { skipToken } from "@reduxjs/toolkit/query";
import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import { useGuestBooking } from "@/components/pages/Guest/GuestBookingContext";
import { useGetClientBookingDataQuery } from "@/redux/apis/clientBooking/clientBookingApi";
import { ClientBookingData } from "@/redux/apis/clientBooking/types";
import { useGetGuestBookingDataQuery } from "@/redux/apis/guest/guestApi";
import { useAppSelector } from "@/redux/hooks";
import { accountIdSelector } from "@/redux/slices/account/selectors";
import { operatorIdSelector } from "@/redux/slices/operator/selectors";

/**
 * Booking reference data (transfer types, vehicles, extras) for the Vehicle & Transfer step, scoped
 * to the current flow: the authed portal reads GET /v3/client/{account_uuid}/job/data, the guest flow
 * (`useGuestBooking().isGuest`) reads GET /v3/guest/provider/{providerUuid}/job/data. Both hydrate the
 * same server-side entity, so each transform yields the SAME `ClientBookingData` shape — one contract
 * for callers either way. The inactive query is skipped so no call fires for the wrong scope.
 *
 * The authed route scopes by account in the PATH: operator OBO uses the managed client's id
 * (`useClientScope().accountId`); self-service falls back to its own active account.
 */
export const useBookingData = (): { data?: ClientBookingData; isLoading: boolean } => {
  const { isGuest } = useGuestBooking();
  const { accountId: managedAccountId } = useClientScope();
  const selfAccountId = useAppSelector(accountIdSelector);
  const providerUuid = useAppSelector(operatorIdSelector);

  // OBO → managed client's account; self-service → own active account. Both go in the URL path.
  const accountId = managedAccountId ?? selfAccountId;

  const client = useGetClientBookingDataQuery(isGuest || !accountId ? skipToken : { accountId });
  const guest = useGetGuestBookingDataQuery(isGuest && providerUuid ? providerUuid : skipToken);

  return isGuest ? { data: guest.data, isLoading: guest.isLoading } : { data: client.data, isLoading: client.isLoading };
};
